import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { BarChartLineIcon, BarChartLineSumIcon, PercentAltIcon } from '@blockworks/ui';

import { VisualizationParameterMeta } from '../../models';
import { ConfigWithExecution, makeGetParameters, makeParameterGroupMeta } from '../utils';

const makeIntervalMeta = (visualization: ConfigWithExecution<StudioVisualizationType.Timeseries>) => {
    const { config, execution } = visualization;
    const { dimensions = {} } = execution.metadata;
    const xColumn = config.options?.xAxis?.column;
    const { enabled, excludes = [], initialValue } = config?.parameters?.timeframe ?? {};
    const { aggregateViews: views = [], duration = 0, basis = 0 } = dimensions[xColumn] ?? {};
    const isActive = views.length > 1;
    const defaultInitialValue = views[0]?.value;

    return {
        id: 'timeframe',
        name: 'Intervals',
        active: isActive,
        enabled: enabled ?? isActive,
        initialValue: initialValue ?? defaultInitialValue,
        views: views,
        hidden: false,
        excludes,
        options: {
            behavior: 'aggregate' as const,
            interval: basis,
            duration,
        },
    } satisfies VisualizationParameterMeta;
};

const makeTransformMeta = (visualization: ConfigWithExecution<StudioVisualizationType.Timeseries>) => {
    const { config } = visualization;
    const { enabled, initialValue, excludes = [] } = config?.parameters?.transform ?? {};
    const stackGroup = config?.options?.groups?.find(group => {
        return (
            ['area', 'bar'].includes(group.type) ||
            group.series.some(series => ['area', 'bar'].includes((series as any).type))
        );
    });

    const defaultInitialValue = 'none';
    return {
        id: 'transform',
        name: 'Stack Transformation',
        active: true,
        enabled: enabled,
        initialValue: initialValue ?? defaultInitialValue,
        hidden: !stackGroup,
        excludes,
        views: [
            { value: 'none', label: 'None', icon: BarChartLineIcon },
            { value: 'cumulative', label: 'Cumulative', icon: BarChartLineSumIcon },
            { value: 'percent', label: 'Percent of Total', icon: PercentAltIcon },
        ],
    } satisfies VisualizationParameterMeta;
};

const getTimeseriesParameters = makeGetParameters([
    makeTransformMeta,
    makeParameterGroupMeta,
    makeIntervalMeta,
])<StudioVisualizationType.Timeseries>;

export { getTimeseriesParameters };
